<template>
	<div
		v-if="ready"
		data-testid="help-assistant-panel"
		:data-chrome="chrome"
		:class="['help-assistant-panel', `help-assistant-panel--${chrome}`]"
	>
		<!--
			No header bar of its own: the Recent toggle lives in the hosting sheet's header, beside
			its close control, which is where the React panel puts it (AB#27211). The host owns the
			open/closed state and passes it down.
		-->
		<help-chat-list
			v-if="chatsOpen"
			id="help-assistant-chats-panel"
			:chats="chats"
			:active-chat-id="chatId"
			:loading="chatsLoading"
			:disabled="streaming"
			@new-chat="handleNewChat"
			@select="handleSelectChat"
			@delete="handleDeleteChat"
		/>

		<help-message-list
			:messages="messages"
			:greeting="greeting"
			:suggested-questions="suggestedQuestions"
			:streaming="streaming"
			:tool-activity="toolActivity"
			:ratings="ratings"
			@retry="handleRetry"
			@rate="handleRate"
			@regenerate="handleRegenerate"
			@followup="handleSend"
			@suggest="handleSend"
		/>

		<div
			v-if="pendingActions.length > 0"
			data-testid="help-assistant-pending-actions"
			class="help-assistant-panel__actions"
		>
			<help-action-card
				v-for="action in pendingActions"
				:key="action.actionId"
				:action="action"
				@confirm="handleConfirmAction"
				@dismiss="handleDismissAction"
			/>
		</div>

		<help-composer
			ref="composer"
			:streaming="streaming"
			:disabled="activeChatDeleting"
			:placeholder="composerPlaceholder"
			@send="handleSend"
			@stop="handleStop"
		/>
	</div>
</template>

<script>
import HelpChatList from './help-chat-list.vue'
import HelpMessageList from './help-message-list.vue'
import HelpActionCard from './help-action-card.vue'
import HelpComposer from './help-composer.vue'
import { createHelpApiClient } from './utils/help-api-client.js'
import { streamHelpChat } from './utils/sse-client.js'
import { runTurn, retryTurn, nextUid } from './utils/run-turn.js'
import { ratingsFromMessages } from './utils/chat-core.js'
import { getAuthToken } from '../../../state/modules/auth.js'
import { getEnvOptional } from '../../../../../shared/utils/runtime-settings.js'
import { HELP_ASSISTANT_GREETING, HELP_ASSISTANT_COMPOSER_PLACEHOLDER } from './utils/assistant-copy.js'

const CHAT_STREAM_PATH = '/api/help/chat'
// How long a resolved action card (applied/error/dismissed) stays visible before it's removed
// from pendingActions, so the outcome is readable rather than vanishing the instant it resolves
// (AB#26561 PR #2723 review).
const ACTION_REMOVAL_DELAY_MS = 4000

/** Session-scoped memory of the active chat. Same key the React panel uses under its default
 * `persistKey`, so a tenant moving between the two portals sees the same continuity rule. */

/**
 * Help-assistant panel orchestrator (AB#26561 Task 8) - a thin shell composing the four
 * presentational children (help-chat-list, help-message-list, help-action-card,
 * help-composer) over the framework-free `run-turn.js` state machine and the
 * help-api-client.js / sse-client.js utils. Holds no business logic of its own beyond wiring:
 * every rule about how a turn streams, splits, persists, errors or retries lives in run-turn.js
 * and chat-core.js, ported for fidelity from the React reference
 * (syrenis-frontends/packages/syrenis-ui/src/components/HelpAssistant/HelpAssistant.tsx).
 *
 * Renders nothing (and warns once) when VUE_APP_BASE_URL_PLATFORMAGENT isn't configured - a
 * missing base URL is an ops/config error, not something to show the end user a broken panel
 * over.
 */
export default {
	name: 'help-assistant-panel',
	components: {
		HelpChatList,
		HelpMessageList,
		HelpActionCard,
		HelpComposer
	},
	props: {
		/** Opening line in the empty state, and the composer placeholder. Both mirror the React
		 * HelpAssistant props of the same purpose: the host says what it can be asked about,
		 * rather than the component assuming one product. */
		greeting: {
			type: String,
			default: HELP_ASSISTANT_GREETING
		},
		/** Starter questions for the empty state. Mirrors the React panel's prop of the same
		 * purpose: the host says what is worth asking here. */
		suggestedQuestions: {
			type: Array,
			default: () => []
		},
		composerPlaceholder: {
			type: String,
			default: HELP_ASSISTANT_COMPOSER_PLACEHOLDER
		},
		/** Screen context threaded through to the stream request and thumbs feedback, so the
		 * server/analytics know which part of the product the question was asked from. */
		context: {
			type: String,
			default: '',
			validator: value => value.length <= 300
		},
		/** Whether the recent-conversations list is showing. Owned by the host, whose sheet header
		 * carries the Recent toggle. */
		chatsOpen: {
			type: Boolean,
			default: false
		},
		/** `panel` is the self-contained flyout look: bordered card on its own background.
		 * `embedded` drops the frame so a host supplies its own, as the splash hero does. */
		chrome: {
			type: String,
			default: 'panel',
			validator: value => ['panel', 'embedded'].includes(value)
		},
		/** Resumes this chat on mount instead of the most recently updated one. */
		initialChatId: {
			type: String,
			default: ''
		},
		/** Sent as the first turn once the resume (or the lack of one) has settled. */
		initialMessage: {
			type: String,
			default: ''
		}
	},
	data () {
		return {
			baseUrl: '',
			api: null,
			chats: [],
			chatsLoading: false,
			chatId: null,
			messages: [],
			streaming: false,
			toolActivity: null,
			pendingActions: [],
			// actionId -> setTimeout id, for the resolved-card removal scheduled in
			// handleConfirmAction/handleDismissAction. Tracked so beforeDestroy and any other
			// pendingActions reset (openChat, handleRetry) can clear a still-pending timeout
			// rather than leaving it to fire against state that's moved on.
			actionTimeouts: {},
			ratings: {},
			abortController: null,
			// Set the moment the user does anything (send/new/select/delete): guards the
			// mount-time auto-resume sequence from clobbering whatever the user has since
			// started, if it's still awaiting a response when one of those fires.
			userActed: false,
			// Chats whose DELETE is in flight, so nothing can be sent into them meanwhile. A list,
			// not one id: the chat list stays usable while a request is pending, so a second delete
			// can start before the first settles.
			deletingChatIds: []
		}
	},
	computed: {
		ready () {
			return this.baseUrl !== ''
		},
		/** True while the active chat is being deleted. The composer is held for that window: a
		 * turn sent then would stream into a chat the server is removing, and the reset that
		 * follows the DELETE would wipe it. Starting a new chat or switching is still allowed. */
		activeChatDeleting () {
			return this.chatId !== null && this.deletingChatIds.includes(this.chatId)
		}
	},
	watch: {
		chats (list) {
			this.$emit('chats-change', list)
		},
		chatId (id) {
			this.$emit('active-chat-change', id)
		},
		streaming (value) {
			this.$emit('streaming-change', value)
		}
	},
	created () {
		this.baseUrl = getEnvOptional('VUE_APP_BASE_URL_PLATFORMAGENT')
		if (!this.baseUrl) {
			// Config missing is an ops error, not a user-facing one - warn once and render
			// nothing rather than showing a panel that can never talk to the server.
			console.warn('help-assistant-panel: VUE_APP_BASE_URL_PLATFORMAGENT is not configured; the help assistant will not render.')
			return
		}
		this.api = createHelpApiClient({ baseUrl: this.baseUrl, getToken: getAuthToken })
		this.initialize()
	},
	beforeDestroy () {
		this.abortController?.abort()
		this.clearActionTimeouts()
	},
	methods: {
		clearActionTimeouts () {
			Object.values(this.actionTimeouts).forEach(id => clearTimeout(id))
			this.actionTimeouts = {}
		},
		scheduleActionRemoval (actionId) {
			if (this.actionTimeouts[actionId]) clearTimeout(this.actionTimeouts[actionId])
			this.actionTimeouts[actionId] = setTimeout(() => {
				this.pendingActions = this.pendingActions.filter(action => action.actionId !== actionId)
				delete this.actionTimeouts[actionId]
			}, ACTION_REMOVAL_DELAY_MS)
		},
		/** Builds a fresh `deps` object for run-turn.js per call, so a changed `context` prop
		 * and a freshly-read auth token are always current rather than captured once at
		 * `created`. */
		buildDeps () {
			return {
				context: this.context || undefined,
				api: this.api,
				stream: options => streamHelpChat({
					endpoint: `${this.baseUrl}${CHAT_STREAM_PATH}`,
					headers: { authorization: `Bearer ${getAuthToken()}` },
					...options
				})
			}
		},
		async loadChats () {
			this.chatsLoading = true
			try {
				this.chats = await this.api.listChats()
			} catch {
				this.chats = []
			} finally {
				this.chatsLoading = false
			}
		},
		async openChat (chatId) {
			let detail
			try {
				detail = await this.api.getChat(chatId)
			} catch {
				// A non-404 failure (network error, 5xx): leave whatever chat was already open
				// alone rather than letting this reject uncaught.
				return
			}
			if (!detail) return
			// A confirmable action card (and any tool-activity hint) from whatever chat was
			// previously open must not survive the switch - it belongs to a turn in a different
			// conversation, and confirming it here would apply a write action against the wrong
			// context (AB#26561 PR #2723 review: cross-chat action leak).
			this.clearActionTimeouts()
			this.pendingActions = []
			this.toolActivity = null
			this.chatId = detail.chatId
			this.messages = detail.messages.map(message => ({
				role: message.role,
				content: message.content,
				id: message.messageId,
				uid: nextUid(),
				timestamp: message.createdAt
			}))
			this.ratings = ratingsFromMessages(detail.messages)
		},
		resetToBlank () {
			this.clearActionTimeouts()
			this.chatId = null
			this.messages = []
			this.ratings = {}
			this.pendingActions = []
			this.toolActivity = null
		},
		/** Auto-resume on mount: load the chat list, then resume a chat the host named, or the most
		 * recently updated one. Bails if the user has meanwhile acted, so a stale resume can't
		 * overwrite a chat/turn they've already started themselves. A host that hands over an
		 * `initialMessage` gets it sent once that has settled, so the first turn lands on whichever
		 * chat this resolves to rather than racing it. */
		async initialize () {
			await this.loadChats()
			if (this.userActed) return
			// Opening the assistant always starts a fresh conversation. It used to adopt the newest
			// chat on the server, then (less badly) whichever chat this browser session last had
			// open, so closing the sheet and reopening it dropped the user back into a conversation
			// they had finished rather than a blank one. Picking a past chat back up is what Recent
			// is for, and it is a deliberate act (AB#27211).
			if (this.initialChatId) {
				await this.openChat(this.initialChatId)
			}
			if (this.initialMessage && !this.userActed) await this.handleSend({ text: this.initialMessage })
		},
		/** Lets a host put the caret in the composer, which the splash hero does when a conversation
		 * opens or is switched. */
		focusComposer () {
			this.$refs.composer?.focus()
		},
		async handleSend ({ text }) {
			// Chips and follow-ups reach here without passing through the disabled composer.
			if (this.activeChatDeleting) return
			this.userActed = true
			const hadChatId = Boolean(this.chatId)
			await runTurn({ state: this, deps: this.buildDeps(), userText: text })
			// The chat list fetched earlier doesn't know about a chat minted just now - refresh
			// so the switcher shows it (real title, updated time) once this turn lands.
			if (!hadChatId && this.chatId) this.loadChats()
		},
		handleRetry ({ index }) {
			if (this.activeChatDeleting) return
			this.userActed = true
			// Action cards don't carry turn identity, so there's no reliable way to tell which
			// pending action(s) belong to the turn being retried - clear all of them rather than
			// risk leaving a stale card behind, or a user confirming one against a turn that
			// retryTurn just dropped (AB#26561 PR #2723 review: coarse-clear is the safe option).
			this.clearActionTimeouts()
			this.pendingActions = []
			return retryTurn({ state: this, deps: this.buildDeps(), index })
		},
		handleRegenerate () {
			// Before the rating is dropped, not after: handleRetry's own guard would otherwise leave
			// the answer unrated and un-regenerated.
			if (this.activeChatDeleting) return
			const index = this.messages.length - 1
			// Mirrors the React reference's regenerateLast: a regenerated answer supersedes
			// whatever thumbs rating was given to the old one, so drop the stale entry rather
			// than leaving it keyed to an answer that no longer exists. Ratings are keyed by
			// the server-assigned messageId, so this only applies once the turn has one.
			const staleMessageId = this.messages[index]?.id
			if (staleMessageId !== undefined) this.$delete(this.ratings, staleMessageId)
			return this.handleRetry({ index })
		},
		handleStop () {
			this.abortController?.abort()
		},
		handleNewChat () {
			this.userActed = true
			// React's HelpAssistant collapses its chat list on both of these; the host owns the
			// open state here, so the panel asks it to close (AB#27211).
			this.$emit('close-chats')
			this.resetToBlank()
		},
		async handleSelectChat ({ chatId }) {
			this.userActed = true
			this.$emit('close-chats')
			await this.openChat(chatId)
		},
		async handleDeleteChat ({ chatId }) {
			// A second confirm for the same chat while its DELETE is outstanding (the row stays until
			// it resolves) would push a duplicate id, and the finally below would then release the
			// guard for both while one request was still in flight. One delete per chat at a time.
			if (this.deletingChatIds.includes(chatId)) return
			this.userActed = true
			this.deletingChatIds.push(chatId)
			try {
				await this.api.deleteChat(chatId)
			} catch {
				return
			} finally {
				this.deletingChatIds = this.deletingChatIds.filter(id => id !== chatId)
			}
			this.chats = this.chats.filter(chat => chat.chatId !== chatId)
			// Compared AFTER the await, not captured before it: focus lands on New chat as the modal
			// closes, so the user can have started a fresh conversation (chatId null) or switched to
			// another before the DELETE returns, and resetting then would wipe what they just began.
			// A turn into the chat itself is blocked meanwhile (activeChatDeleting), so what is reset
			// here is only what predates the delete.
			if (chatId === this.chatId) this.resetToBlank()
		},
		async handleRate ({ messageId, rating }) {
			if (!this.chatId || messageId === undefined || this.ratings[messageId]) return
			this.$set(this.ratings, messageId, rating)
			const accepted = await this.api.sendFeedback(this.chatId, { messageId, rating, screenContext: this.context })
			if (!accepted) this.$delete(this.ratings, messageId)
		},
		async handleConfirmAction ({ actionId }) {
			const action = this.pendingActions.find(item => item.actionId === actionId)
			if (!action) return
			this.$set(action, 'status', 'confirming')
			const applied = await this.api.confirmAction(actionId)
			this.$set(action, 'status', applied ? 'applied' : 'error')
			this.scheduleActionRemoval(actionId)
		},
		handleDismissAction ({ actionId }) {
			const action = this.pendingActions.find(item => item.actionId === actionId)
			if (!action) return
			this.$set(action, 'status', 'dismissed')
			this.api.dismissAction(actionId)
			this.scheduleActionRemoval(actionId)
		}
	}
}
</script>

<style lang="scss" scoped>
.help-assistant-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--card, #fff);
  border: 1px solid var(--border, #dfe4ea);
  border-radius: var(--radius, 8px);

  // `embedded` hands the frame to the host, which is what the splash hero wants: one bordered
  // surface of its own holding the transcript and composer (AB#27211). Declared after the base so
  // it wins the cascade, since both selectors carry the same specificity.
  &--embedded {
    min-height: 0;
    background: none;
    border: none;
    border-radius: 0;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 16px 12px;
  }
}
</style>
